// src/components/admin/LessonForm.jsx
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Plus } from 'lucide-react';

// 👇 DOMANDE PREDEFINITE per il nuovo tipo "goals_rewards"
const DEFAULT_GOALS_REWARDS = [
  { id: "ricompense_header", type: "header", question: "Ricompense e Obiettivi" },
  { id: "ricompensa_1_giorno",      type: "text", question: "Cosa ti regalerai dopo 1 giorno senza fumare?" },
  { id: "ricompensa_3_giorni",      type: "text", question: "Cosa ti regalerai dopo 3 giorni senza fumare?" },
  { id: "ricompensa_1_settimana",   type: "text", question: "Cosa ti regalerai dopo 1 settimana senza fumare?" },
  { id: "ricompensa_2_settimane",   type: "text", question: "Cosa ti regalerai dopo 2 settimane senza fumare?" },
  { id: "ricompensa_1_mese",        type: "text", question: "Cosa ti regalerai dopo 1 mese senza fumare?" },
  { id: "ricompensa_2_mesi",        type: "text", question: "Cosa ti regalerai dopo 2 mesi senza fumare?" },
  { id: "ricompensa_6_mesi",        type: "text", question: "Cosa ti regalerai dopo 6 mesi senza fumare?" },
  { id: "ricompensa_1_anno",        type: "text", question: "Cosa ti regalerai dopo 1 anno senza fumare?" },
  { id: "ricompensa_2_anni",        type: "text", question: "Cosa ti regalerai dopo 2 anni senza fumare?" },
  { id: "ricompensa_5_anni",        type: "text", question: "Cosa ti regalerai dopo 5 anni senza fumare?" },
];

export default function LessonForm({ lesson, onSubmit, onCancel }) {
  const [formData, setFormData] = useState(
    lesson || {
      title: '',
      description: '',
      audio_url: '',
      duration: 0,
      order: 0,
      exercise_instructions: '',
      exercise_question: '',
      exercise_type: 'text_input',
      exercise_options: [],
      exercise_structured_questions: [],
      scored_test_multiple_choice: [],
      scored_test_open_questions: [],
      background_audio_options: [],
    }
  );

  const [jsonText, setJsonText] = useState(
    JSON.stringify(formData.exercise_structured_questions, null, 2)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // 👇 Manteniamo in sync l'editor JSON con lo stato
    if (field === 'exercise_structured_questions' || field === 'exercise_type') {
      // Se l'utente seleziona "goals_rewards" e non ci sono domande, precompiliamo
      if (field === 'exercise_type' && value === 'goals_rewards' &&
          (!formData.exercise_structured_questions || formData.exercise_structured_questions.length === 0)) {
        setFormData(prev => ({ ...prev, exercise_structured_questions: DEFAULT_GOALS_REWARDS }));
        setJsonText(JSON.stringify(DEFAULT_GOALS_REWARDS, null, 2));
        return;
      }

      const source =
        field === 'exercise_structured_questions'
          ? value
          : formData.exercise_structured_questions;

      setJsonText(JSON.stringify(source, null, 2));
    }
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonText(value);

    if (value.trim() === '') {
      updateField('exercise_structured_questions', []);
      return;
    }
    if (value.trim().startsWith('[') && value.trim().endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        updateField('exercise_structured_questions', parsed);
      } catch {}
    }
  };

  const addBackgroundAudio = () => {
    const newOptions = [...(formData.background_audio_options || []), { name: '', url: '' }];
    updateField('background_audio_options', newOptions);
  };

  const removeBackgroundAudio = (index) => {
    const newOptions = formData.background_audio_options.filter((_, i) => i !== index);
    updateField('background_audio_options', newOptions);
  };

  const updateBackgroundAudio = (index, field, value) => {
    const newOptions = [...formData.background_audio_options];
    newOptions[index][field] = value;
    updateField('background_audio_options', newOptions);
  };

  const addMultipleChoiceQuestion = () => {
    const newQuestions = [...(formData.scored_test_multiple_choice || []), { question: '', options: ['', '', ''] }];
    updateField('scored_test_multiple_choice', newQuestions);
  };

  const removeMultipleChoiceQuestion = (index) => {
    const newQuestions = formData.scored_test_multiple_choice.filter((_, i) => i !== index);
    updateField('scored_test_multiple_choice', newQuestions);
  };

  const updateMultipleChoiceQuestion = (index, field, value) => {
    const newQuestions = [...formData.scored_test_multiple_choice];
    newQuestions[index][field] = value;
    updateField('scored_test_multiple_choice', newQuestions);
  };

  const updateMultipleChoiceOption = (qIndex, oIndex, value) => {
    const newQuestions = [...formData.scored_test_multiple_choice];
    newQuestions[qIndex].options[oIndex] = value;
    updateField('scored_test_multiple_choice', newQuestions);
  };

  const addOptionToQuestion = (qIndex) => {
    const newQuestions = [...formData.scored_test_multiple_choice];
    newQuestions[qIndex].options.push('');
    updateField('scored_test_multiple_choice', newQuestions);
  };

  const removeOptionFromQuestion = (qIndex, oIndex) => {
    const newQuestions = [...formData.scored_test_multiple_choice];
    newQuestions[qIndex].options.splice(oIndex, 1);
    updateField('scored_test_multiple_choice', newQuestions);
  };

  const addOpenQuestion = () => {
    const newQuestions = [...(formData.scored_test_open_questions || []), ''];
    updateField('scored_test_open_questions', newQuestions);
  };

  const updateOpenQuestion = (index, value) => {
    const newQuestions = [...(formData.scored_test_open_questions || [])];
    newQuestions[index] = value;
    updateField('scored_test_open_questions', newQuestions);
  };

  const removeOpenQuestion = (index) => {
    const newQuestions = (formData.scored_test_open_questions || []).filter((_, i) => i !== index);
    updateField('scored_test_open_questions', newQuestions);
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>
          {lesson ? 'Modifica Lezione' : 'Crea Nuova Lezione'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Titolo</Label>
              <Input
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => updateField('order', Number(e.target.value))}
                required
              />
            </div>
            <div>
              <Label>URL Audio</Label>
              <Input
                value={formData.audio_url}
                onChange={(e) => updateField('audio_url', e.target.value)}
              />
            </div>
            <div>
              <Label>Durata (minuti)</Label>
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => updateField('duration', Number(e.target.value))}
              />
            </div>
          </div>

          <div>
            <Label>Descrizione</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
            />
          </div>

          <div className="border p-4 rounded-lg space-y-4">
            <h3 className="font-semibold">Dettagli Esercizio</h3>

            <div>
              <Label>Istruzioni Esercizio</Label>
              <Textarea
                value={formData.exercise_instructions}
                onChange={(e) => updateField('exercise_instructions', e.target.value)}
              />
            </div>

            <div>
              <Label>Domanda Esercizio</Label>
              <Input
                value={formData.exercise_question}
                onChange={(e) => updateField('exercise_question', e.target.value)}
              />
            </div>

            <div>
              <Label>Tipo Esercizio</Label>
              <Select
                value={formData.exercise_type}
                onValueChange={(value) => updateField('exercise_type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text_input">Testo Libero</SelectItem>
                  <SelectItem value="reflection">Riflessione</SelectItem>
                  <SelectItem value="breathing">Respirazione</SelectItem>
                  <SelectItem value="multiple_choice">Scelta Multipla</SelectItem>

                  {/* 👇 NUOVO TIPO: apparirà come voce separata */}
                  <SelectItem value="goals_rewards">Obiettivi & Ricompense</SelectItem>

                  {/* tipo classico, rimane disponibile */}
                  <SelectItem value="structured_questionnaire">Questionario Strutturato</SelectItem>

                  <SelectItem value="scored_test">Test con Punteggio + Causa Scatenante</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* multiple_choice */}
            {formData.exercise_type === 'multiple_choice' && (
              <div>
                <Label>Opzioni (una per riga)</Label>
                <Textarea
                  value={formData.exercise_options.join('\n')}
                  onChange={(e) => updateField('exercise_options', e.target.value.split('\n'))}
                />
              </div>
            )}

            {/* goals_rewards e structured_questionnaire condividono lo stesso editor JSON */}
            {(formData.exercise_type === 'structured_questionnaire' ||
              formData.exercise_type === 'goals_rewards') && (
              <div>
                <Label>
                  {formData.exercise_type === 'goals_rewards'
                    ? 'Obiettivi & Ricompense (JSON delle domande)'
                    : 'Struttura Questionario (JSON)'}
                </Label>
                <Textarea
                  value={jsonText}
                  onChange={handleJsonChange}
                  rows={10}
                  placeholder='[{"id":"ricompensa_1_giorno","type":"text","question":"Cosa ti regalerai dopo 1 giorno senza fumare?"}]'
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    Inserisci un array JSON valido. Il sistema salva quando la sintassi è corretta.
                  </p>
                  {formData.exercise_type === 'goals_rewards' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        updateField('exercise_structured_questions', DEFAULT_GOALS_REWARDS);
                        setJsonText(JSON.stringify(DEFAULT_GOALS_REWARDS, null, 2));
                      }}
                    >
                      Usa preset 12 obiettivi
                    </Button>
                  )}
                </div>
              </div>
            )}

            {formData.exercise_type === 'scored_test' && (
              <div className="space-y-6">
                <div className="border p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold">Domande a Scelta Multipla (con punteggio)</h4>
                    <Button type="button" variant="outline" onClick={addMultipleChoiceQuestion}>
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi Domanda
                    </Button>
                  </div>

                  {(formData.scored_test_multiple_choice || []).map((question, qIndex) => (
                    <div key={qIndex} className="border p-3 rounded mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <Label>Domanda {qIndex + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMultipleChoiceQuestion(qIndex)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        value={question.question}
                        onChange={(e) => updateMultipleChoiceQuestion(qIndex, 'question', e.target.value)}
                        placeholder="Inserisci la domanda"
                        className="mb-3"
                      />

                      <div className="space-y-2">
                        <Label className="text-sm">Opzioni di risposta (la prima vale il punteggio massimo)</Label>
                        {(question.options || []).map((option, oIndex) => (
                          <div key={oIndex} className="flex gap-2 items-center">
                            <span className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-sm font-medium">
                              {String.fromCharCode(97 + oIndex)}
                            </span>
                            <Input
                              value={option}
                              onChange={(e) => updateMultipleChoiceOption(qIndex, oIndex, e.target.value)}
                              placeholder={`Opzione ${String.fromCharCode(97 + oIndex)}`}
                            />
                            {(question.options || []).length > 2 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeOptionFromQuestion(qIndex, oIndex)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addOptionToQuestion(qIndex)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Opzione
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border p-4 rounded-lg space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold">Domande a Risposta Aperta</h4>
                    <Button type="button" variant="outline" onClick={addOpenQuestion}>
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi Domanda
                    </Button>
                  </div>

                  {(formData.scored_test_open_questions || []).map((question, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={question}
                        onChange={(e) => updateOpenQuestion(index, e.target.value)}
                        placeholder={`Domanda aperta ${index + 1}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeOpenQuestion(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border p-4 rounded-lg space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Audio di Sottofondo</h3>
              <Button type="button" variant="outline" onClick={addBackgroundAudio}>
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi
              </Button>
            </div>

            {(formData.background_audio_options || []).map((option, index) => (
              <div key={index} className="flex gap-2 items-center">
                <Input
                  placeholder="Nome"
                  value={option.name}
                  onChange={(e) => updateBackgroundAudio(index, 'name', e.target.value)}
                />
                <Input
                  placeholder="URL"
                  value={option.url}
                  onChange={(e) => updateBackgroundAudio(index, 'url', e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeBackgroundAudio(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Annulla
            </Button>
            <Button type="submit">
              Salva Lezione
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
